import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
    container: {
        marginBottom: 15,
    },
    sectionHeader: {
        fontSize: 16,
        color: '#004899',
        fontWeight: 'bold',
        marginBottom: 10,
        backgroundColor: '#f8f9fa',
        padding: 8,
        borderRadius: 4,
        borderLeftWidth: 3,
        borderLeftColor: '#004899',
    },
    tabsContainer: {
        flexDirection: 'row',
        marginBottom: 10,
        flexWrap: 'wrap',
    },
    tab: {
        backgroundColor: '#004899',
        color: 'white',
        padding: 6,
        marginRight: 6,
        marginBottom: 4,
        borderRadius: 3,
        fontSize: 9,
        fontWeight: 'bold',
        minWidth: 60,
        textAlign: 'center',
    },
    modeContainer: {
        marginBottom: 15,
        borderWidth: 1,
        borderColor: '#e0e0e0',
        borderRadius: 4,
        overflow: 'hidden',
    },
    modeHeader: {
        backgroundColor: '#004899',
        padding: 6,
        color: 'white',
        fontSize: 12,
        fontWeight: 'bold',
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#f0f5ff',
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
    },
    headerCell: {
        width: '25%',
        padding: 6,
        fontSize: 8,
        fontWeight: 'bold',
        color: '#004899',
        borderRightWidth: 1,
        borderRightColor: '#e0e0e0',
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
    },
    tableCell: {
        width: '25%',
        padding: 6,
        fontSize: 8,
        borderRightWidth: 1,
        borderRightColor: '#e0e0e0',
    },
    lastCell: {
        borderRightWidth: 0,
    },
    additionalInfo: {
        padding: 6,
        backgroundColor: '#f9f9f9',
        borderTopWidth: 1,
        borderTopColor: '#e0e0e0',
    },
    infoRow: {
        flexDirection: 'row',
        marginBottom: 4,
    },
    infoLabel: {
        width: '35%',
        fontSize: 8,
        fontWeight: 'bold',
        color: '#004899',
    },
    infoValue: {
        width: '65%',
        fontSize: 8,
    },
});

interface ShippingMode {
    mode: number;
    transitMode: string;
    startCity: string;
    startStateName: string;
    startZip: string;
    endCity: string;
    endStateName: string;
    endZip: string;
    distanceTravelled: string;
    elevationChanges: string;
    elevationMax?: string;
    intermediateStopNotes?: string;
}

interface NewShippingModesSectionProps {
    shippingModes: ShippingMode[];
}

export const NewShippingModesSection: React.FC<NewShippingModesSectionProps> = ({ shippingModes }) => (
    <View style={styles.container} wrap={false}>
        <Text style={styles.sectionHeader}>Shipping Modes</Text>

        <View style={styles.tabsContainer}>
            {shippingModes.map((mode) => (
                <Text key={`tab-${mode.mode}`} style={styles.tab}>
                    Mode {mode.mode}
                </Text>
            ))}
        </View>

        {shippingModes.map((mode, index) => (
            <View key={`mode-${mode.mode}`} style={styles.modeContainer} wrap={false}>
                <Text style={styles.modeHeader}>Mode {mode.mode} - {mode.transitMode}</Text>

                {/* First row of the table */}
                <View style={styles.tableHeader}>
                    <Text style={styles.headerCell}>Transit Mode</Text>
                    <Text style={styles.headerCell}>Start City</Text>
                    <Text style={styles.headerCell}>Start State</Text>
                    <Text style={[styles.headerCell, styles.lastCell]}>Start Zip</Text>
                </View>
                <View style={styles.tableRow}>
                    <Text style={styles.tableCell}>{mode.transitMode}</Text>
                    <Text style={styles.tableCell}>{mode.startCity}</Text>
                    <Text style={styles.tableCell}>{mode.startStateName}</Text>
                    <Text style={[styles.tableCell, styles.lastCell]}>{mode.startZip}</Text>
                </View>

                {/* Second row of the table */}
                <View style={styles.tableHeader}>
                    <Text style={styles.headerCell}>Distance (miles)</Text>
                    <Text style={styles.headerCell}>End City</Text>
                    <Text style={styles.headerCell}>End State</Text>
                    <Text style={[styles.headerCell, styles.lastCell]}>End Zip</Text>
                </View>
                <View style={styles.tableRow}>
                    <Text style={styles.tableCell}>{mode.distanceTravelled}</Text>
                    <Text style={styles.tableCell}>{mode.endCity}</Text>
                    <Text style={styles.tableCell}>{mode.endStateName}</Text>
                    <Text style={[styles.tableCell, styles.lastCell]}>{mode.endZip}</Text>
                </View>

                {/* Additional information section */}
                <View style={styles.additionalInfo}>
                    <View style={styles.infoRow}>
                        <Text style={styles.infoLabel}>Elevation Changes:</Text>
                        <Text style={styles.infoValue}>{mode.elevationChanges}</Text>
                    </View>

                    {mode.elevationChanges === 'Yes' && (
                        <View style={styles.infoRow}>
                            <Text style={styles.infoLabel}>Elevation Max:</Text>
                            <Text style={styles.infoValue}>{mode.elevationMax} ft</Text>
                        </View>
                    )}

                    {mode.intermediateStopNotes && (
                        <View style={styles.infoRow}>
                            <Text style={styles.infoLabel}>Intermediate Stops:</Text>
                            <Text style={styles.infoValue}>{mode.intermediateStopNotes}</Text>
                        </View>
                    )}
                </View>
            </View>
        ))}
    </View>
); 